import { createContext, useContext } from 'react';
import { API } from '@shared/types/api';

export interface CardNavigationItem {
  entityId: string;
  channelSlug: string;
  entityVariables?: API.ListWorkflowEntitiesQueryVariables;
  // explicit prev/next order, for a view whose rows aren't the entityVariables
  // query's own order (the tree flattens a hierarchy of separate level queries)
  entityIds?: string[];
  backTooltip?: string;
}

export interface CardNavigationState {
  stack: CardNavigationItem[];
  currentItem?: CardNavigationItem;
  previousItem?: CardNavigationItem;
}

// Split from the state on purpose: the actions are stable for the provider's
// lifetime, while the stack changes on every card open. Consumers that only
// push onto the stack (every collection view group's open handler) would
// otherwise re-render on each stack change.
export interface CardNavigationActions {
  addStackItem: (item: CardNavigationItem) => void;
  updateCurrentItem: (target: CardNavigationItem) => void;
  removeCurrentItemAfterNavigation: () => void;
  resetStackAfterNavigation: () => void;
}

const defaultCardNavigationState: CardNavigationState = {
  stack: [],
  currentItem: undefined,
  previousItem: undefined,
};

const defaultCardNavigationActions: CardNavigationActions = {
  addStackItem: () => undefined,
  updateCurrentItem: () => undefined,
  removeCurrentItemAfterNavigation: () => undefined,
  resetStackAfterNavigation: () => undefined,
};

export const CardNavigationStateContext = createContext<CardNavigationState>(
  defaultCardNavigationState,
);

export const CardNavigationActionsContext = createContext<CardNavigationActions>(
  defaultCardNavigationActions,
);

export function useCardNavigationState(): CardNavigationState {
  return useContext(CardNavigationStateContext);
}

export function useCardNavigationActions(): CardNavigationActions {
  return useContext(CardNavigationActionsContext);
}
